import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Clapperboard, FileText, MapPin, Package, Settings2, Users } from 'lucide-react';

import AssetBrowser from '@/components/AssetBrowser';
import SharePanel from '@/components/SharePanel';
import { Badge } from '@/components/ui/badge';
import SectionRail, { type RailSection } from '@/components/ui/SectionRail';
import Spinner from '@/components/ui/Spinner';
import AssetRoster from '@/components/project/AssetRoster';
import NewProductionDialog from '@/components/project/NewProductionDialog';
import ProductionsList from '@/components/project/ProductionsList';
import ProjectHeader from '@/components/project/ProjectHeader';
import ProjectSettingsCard from '@/components/project/ProjectSettingsCard';
import SourceMaterial from '@/components/project/SourceMaterial';
import TranslationReportBanner from '@/components/project/TranslationReportBanner';
import SetupChecklist from '@/components/project/SetupChecklist';
import { useBibleGeneration } from '@/hooks/useBibleGeneration';
import { useScriptUpload } from '@/hooks/useScriptUpload';
import { useProject } from '@/hooks/useProject';
import { productionReadiness } from '@/lib/projectReadiness';

const ROSTER_ICONS = {
  characters: <Users className="h-4 w-4" />,
  locations: <MapPin className="h-4 w-4" />,
  props: <Package className="h-4 w-4" />,
} as const;

/**
 * A project's home.
 *
 * Sectioned behind a vertical rail (matching the production console) rather than
 * stacked in one page: the old layout crammed six unrelated concerns into a narrow
 * right column while the left held two mostly-empty cards, so the page was both very
 * tall and half dead space. Each section now gets the full width.
 */
export default function ProjectDashboard() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['dashboard', 'common']);
  const {
    project,
    setProject,
    loading,
    assets,
    rosters,
    genres,
    styles,
    toast,
    refresh,
    patchProject,
    renameProjectTitle,
    removeProject,
    uploadBrief,
    toggleEntry,
    removeEntry,
    uploadSheet,
    promoteCharacter,
  } = useProject(id);

  const [section, setSection] = useState('source');
  const [launching, setLaunching] = useState(false);
  const { generating, generate } = useBibleGeneration(id);
  const { uploading, upload: uploadScript } = useScriptUpload(id, refresh);

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }
  if (!project || !id) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="text-muted-foreground">{t('project.notFound')}</p>
      </div>
    );
  }

  const isFootage = (project.production_type || 'generative') === 'footage';
  const readiness = productionReadiness(project);
  const productions = project.productions || [];
  const castCount = rosters.characters.length + rosters.locations.length + rosters.props.length;

  const sections: RailSection[] = [
    {
      key: 'source',
      label: t('section.source'),
      status: t(`readiness.short.${readiness.reason}`),
      icon: <FileText className="h-4 w-4" />,
    },
    // Cast/sets/props are generation machinery — a footage production designs none.
    ...(isFootage
      ? []
      : [
          {
            key: 'cast',
            label: t('section.cast'),
            status: castCount > 0 ? t('section.castCount', { count: castCount }) : t('section.castEmpty'),
            icon: <Users className="h-4 w-4" />,
          },
        ]),
    {
      key: 'productions',
      label: t('section.productions'),
      status: t('project.productionCount', { count: productions.length }),
      icon: <Clapperboard className="h-4 w-4" />,
    },
    {
      key: 'settings',
      label: t('section.settings'),
      status: t('section.settingsHint'),
      icon: <Settings2 className="h-4 w-4" />,
    },
  ];

  // A footage project has no cast section, so a stale selection must fall back.
  const active = sections.some((s) => s.key === section) ? section : 'source';

  return (
    <div className="mx-auto max-w-6xl px-8 py-7">
      {toast && (
        <div className="fixed right-4 top-4 z-50 max-w-sm rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background shadow-lg">
          {toast}
        </div>
      )}

      <ProjectHeader
        project={project}
        readiness={readiness}
        onNewProduction={() => setLaunching(true)}
        onRename={renameProjectTitle}
        onDelete={removeProject}
      />

      {/* Setup first, then the bible's validation report — the checklist tells you
          WHAT to do; the report explains a failure once you've tried. */}
      <div className="mt-6 space-y-4">
        <SetupChecklist
          project={project}
          readiness={readiness}
          generating={generating}
          uploading={uploading}
          onGenerateBible={generate}
          onUploadScript={uploadScript}
          onUploadBrief={uploadBrief}
          onGoToSettings={() => setSection('settings')}
        />
        <TranslationReportBanner report={project.translation_report} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[200px_minmax(0,1fr)]">
        <div className="lg:sticky lg:top-4 lg:self-start">
          <SectionRail title={t('section.rail')} sections={sections} activeKey={active} onSelect={setSection} />
        </div>

        <div className="min-w-0">
          {active === 'source' && <SourceMaterial project={project} onUploaded={refresh} onUploadBrief={uploadBrief} />}

          {active === 'cast' && (
            <div className="space-y-5">
              {(['characters', 'locations', 'props'] as const).map((kind) => (
                <AssetRoster
                  key={kind}
                  kind={kind}
                  title={t(`roster.${kind}`)}
                  icon={ROSTER_ICONS[kind]}
                  help={t(`roster.${kind}Help`)}
                  addPlaceholder={t(`roster.${kind}Add`)}
                  emptyHint={t(`roster.${kind}Empty`)}
                  projectId={id}
                  entries={rosters[kind]}
                  onToggle={toggleEntry}
                  onRemove={removeEntry}
                  onUpload={uploadSheet}
                />
              ))}
            </div>
          )}

          {active === 'productions' && (
            <div className="space-y-5">
              <ProductionsList
                projectId={id}
                productions={productions}
                onDeleted={(prodId) =>
                  setProject({
                    ...project,
                    productions: productions.filter((p) => p.id !== prodId),
                    production_count: Math.max(0, project.production_count - 1),
                  })
                }
              />
              {Object.entries(assets).map(([prodId, list]) =>
                list.length > 0 ? (
                  <div key={prodId}>
                    <div className="mb-1.5 flex items-center gap-2 font-mono text-xs text-faint">
                      <span>{t('assets.production', { id: prodId.slice(-6) })}</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {t('assets.count', { count: list.length })}
                      </Badge>
                    </div>
                    <AssetBrowser
                      productionId={prodId}
                      availableAssets={list}
                      onPromoteCharacter={(name) => promoteCharacter(prodId, name)}
                    />
                  </div>
                ) : null,
              )}
            </div>
          )}

          {active === 'settings' && (
            <div className="space-y-5">
              <ProjectSettingsCard project={project} onPatch={patchProject} />
              <SharePanel projectId={project.id} ownerUid={project.owner_uid || ''} />
            </div>
          )}
        </div>
      </div>

      <NewProductionDialog project={project} genres={genres} styles={styles} open={launching} onOpenChange={setLaunching} />
    </div>
  );
}
